import { useState } from "react";
import { motion } from "framer-motion";
import { FileText, Download, Lock, CheckCircle, User, Mail, Phone, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { submitLead } from "@/lib/api";
import { toast } from "sonner";

export const BrochureSection = ({ brochures, verticalName = "" }) => {
  const [selectedBrochure, setSelectedBrochure] = useState(null);
  const [isGateOpen, setIsGateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "" });

  if (!brochures || brochures.length === 0) return null;

  const handleDownloadClick = (brochure) => {
    setSelectedBrochure(brochure);
    setIsGateOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await submitLead({
        ...formData,
        problem_interest: `Brochure: ${selectedBrochure?.title}`,
        vertical_interest: verticalName,
        message: `Downloaded brochure: ${selectedBrochure?.title}`
      });
      
      toast.success("Thank you! Your download will start shortly.");
      setIsGateOpen(false);
      
      // Trigger download
      if (selectedBrochure?.file_url) {
        window.open(selectedBrochure.file_url, '_blank');
      }
      
      setFormData({ name: "", email: "", phone: "", company: "" });
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="brochure-section">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {brochures.map((brochure, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="group relative rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg transition-all"
          >
            {/* Thumbnail */}
            <div className="aspect-[4/3] bg-gradient-to-br from-[#0066CC]/10 to-[#0066CC]/5 flex items-center justify-center relative">
              {brochure.thumbnail_url ? (
                <img src={brochure.thumbnail_url} alt={brochure.title} className="w-full h-full object-cover" />
              ) : (
                <FileText className="w-16 h-16 text-[#0066CC]/40" />
              )}
              {/* Lock Overlay */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <div className="text-white text-center">
                  <Lock className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">Provide contact info to download</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4">
              <h4 className="font-semibold text-slate-900 mb-1">{brochure.title}</h4>
              {brochure.description && (
                <p className="text-sm text-slate-600 mb-3 line-clamp-2">{brochure.description}</p>
              )}
              <Button 
                onClick={() => handleDownloadClick(brochure)}
                className="w-full bg-[#0066CC] hover:bg-[#0052A3] gap-2"
                data-testid={`download-brochure-${index}`}
              >
                <Download className="w-4 h-4" />
                Download Free
              </Button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Lead Capture Gate Dialog */}
      <Dialog open={isGateOpen} onOpenChange={setIsGateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#0066CC]" />
              Download: {selectedBrochure?.title}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            <p className="text-sm text-slate-600">
              Please provide your contact details to download this resource.
            </p>
            
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-sm flex items-center gap-1"><User className="w-3 h-3" /> Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your full name"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm flex items-center gap-1"><Mail className="w-3 h-3" /> Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="work@company.com"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm flex items-center gap-1"><Phone className="w-3 h-3" /> Phone *</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm flex items-center gap-1"><Building className="w-3 h-3" /> Company</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Company name"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full bg-[#0066CC] hover:bg-[#0052A3] gap-2"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </span>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Submit & Download
                </>
              )}
            </Button>
            
            <p className="text-xs text-slate-500 text-center">
              We respect your privacy. Your information is secure with us.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
